import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Chip,
  Stack,
  Divider,
  Paper
} from '@mui/material';
import {
  Payment as PaymentIcon,
  Launch as LaunchIcon,
  ArrowBack as ArrowBackIcon,
  Refresh as RefreshIcon,
  SwapHoriz as SwapIcon
} from '@mui/icons-material';
import { publicPaymentAPI } from '../services/tripayService';
import { BillForPayment, PaymentMethod, PaymentCreationInput, PaymentStatusResponse } from '../types/tripay';

const PublicPaymentPage: React.FC = () => {
  const { billId } = useParams<{ billId: string }>();
  const navigate = useNavigate();
  
  const [bill, setBill] = useState<BillForPayment | null>(null);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [paymentStatus, setPaymentStatus] = useState<PaymentStatusResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [step, setStep] = useState<'form' | 'payment-created' | 'check-status' | 'change-method'>('form');
  const [showChangeMethod, setShowChangeMethod] = useState(false);
  
  const [formData, setFormData] = useState<PaymentCreationInput>({
    bill_id: billId || '',
    payment_method: '',
    customer_name: '',
    customer_email: '',
    customer_phone: ''
  });

  const [newPaymentMethod, setNewPaymentMethod] = useState('');

  useEffect(() => {
    if (billId) {
      loadBillData();
      loadPaymentMethods();
      checkExistingPayment();
    }
  }, [billId]);

  const loadBillData = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await publicPaymentAPI.getBillDetail(billId!);
      
      if (response.success) {
        setBill(response.data);
        setFormData(prev => ({
          ...prev,
          bill_id: response.data.id,
          customer_name: response.data.customer?.name || '',
          customer_email: response.data.customer?.email || '',
          customer_phone: response.data.customer?.phone || ''
        }));
      } else {
        setError(response.message || 'Tagihan tidak ditemukan');
      }
    } catch (err: any) {
      setError(err.message || 'Gagal memuat data tagihan');
    } finally {
      setLoading(false);
    }
  };

  const checkExistingPayment = async () => {
    try {
      const response = await publicPaymentAPI.checkExistingPayment(billId!);
      
      if (response.success && response.data) {
        const existingPayment = response.data;
        console.log('Existing payment data:', existingPayment);
        
        // Check if payment is still valid (not expired and status UNPAID)
        if (!existingPayment.is_expired && existingPayment.status === 'UNPAID') {
          setPaymentStatus({
            reference: existingPayment.merchant_ref,
            status: existingPayment.status,
            payment_method_name: existingPayment.payment_method_name || existingPayment.payment_method || 'Tidak Diketahui',
            amount: existingPayment.amount,
            expired_time: existingPayment.expired_time,
            paid_time: undefined,
            checkout_url: existingPayment.checkout_url,
            pay_code: existingPayment.pay_code,
            pay_url: existingPayment.pay_url
          });
          setStep('payment-created');
        } else if (existingPayment.status === 'PAID') {
          setPaymentStatus({
            reference: existingPayment.merchant_ref,
            status: existingPayment.status,
            payment_method_name: existingPayment.payment_method_name || existingPayment.payment_method || 'Tidak Diketahui',
            amount: existingPayment.amount,
            expired_time: existingPayment.expired_time,
            paid_time: existingPayment.paid_time || undefined,
            checkout_url: existingPayment.checkout_url,
            pay_code: existingPayment.pay_code,
            pay_url: existingPayment.pay_url
          });
          setStep('payment-created');
          setSuccess('Pembayaran telah berhasil diselesaikan!');
        }
      }
    } catch (err: any) {
      console.error('Error checking existing payment:', err);
    }
  };

  const loadPaymentMethods = async () => {
    try {
      const response = await publicPaymentAPI.getPaymentMethods();
      
      if (response.success) {
        setPaymentMethods(response.data.filter((method: PaymentMethod) => method.active));
      }
    } catch (err: any) {
      console.error('Gagal memuat metode pembayaran:', err);
    }
  };

  const handleInputChange = (field: keyof PaymentCreationInput) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value as string
    }));
  };

  const handleSelectChange = (field: keyof PaymentCreationInput) => (
    event: any
  ) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value as string
    }));
  };

  const handleCreatePayment = async () => {
    if (!formData.payment_method || !formData.customer_name || !formData.customer_email) {
      setError('Silakan lengkapi semua field yang diperlukan');
      return;
    }

    try {
      setCreating(true);
      setError('');
      setSuccess('');

      const response = await publicPaymentAPI.createPayment(formData);

      if (response.success) {
        setPaymentStatus({
          reference: response.data.reference,
          status: response.data.status,
          payment_method_name: response.data.payment_method_name,
          amount: response.data.amount,
          expired_time: response.data.expired_time,
          paid_time: response.data.paid_time,
          checkout_url: response.data.checkout_url,
          pay_code: response.data.pay_code,
          pay_url: response.data.pay_url
        });
        setStep('payment-created');
        setSuccess('Pembayaran berhasil dibuat! Silakan selesaikan pembayaran Anda.');
      } else {
        setError(response.message || 'Gagal membuat pembayaran');
      }
    } catch (err: any) {
      setError(err.message || 'Gagal membuat pembayaran');
    } finally {
      setCreating(false);
    }
  };

  const handleCheckStatus = async (reference: string) => {
    try {
      setLoading(true);
      const response = await publicPaymentAPI.checkPaymentStatus(reference);
      
      if (response.success) {
        setPaymentStatus(response.data);
        
        if (response.data.status === 'PAID') {
          setSuccess('Pembayaran berhasil! Terima kasih atas pembayaran Anda.');
        } else if (response.data.status === 'EXPIRED') {
          setError('Pembayaran telah kedaluwarsa. Silakan buat pembayaran baru.');
          setStep('form');
        } else if (response.data.status === 'FAILED') {
          setError('Pembayaran gagal. Silakan coba lagi.');
          setStep('form');
        }
      } else {
        setError(response.message || 'Gagal memeriksa status pembayaran');
      }
    } catch (err: any) {
      setError(err.message || 'Gagal memeriksa status pembayaran');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'UNPAID': return 'warning';
      case 'PAID': return 'success';
      case 'EXPIRED': return 'error';
      case 'FAILED': return 'error';
      default: return 'default';
    }
  };

  const handleCreateNewPayment = () => {
    setStep('form');
    setPaymentStatus(null);
    setError('');
    setSuccess('');
  };

  const refreshPaymentStatus = async () => {
    if (paymentStatus?.reference) {
      await handleCheckStatus(paymentStatus.reference);
    }
  };

  const handleChangePaymentMethod = () => {
    setShowChangeMethod(true);
    setNewPaymentMethod('');
    setError('');
    setSuccess('');
    
    // Load payment methods if not loaded yet
    if (paymentMethods.length === 0) {
      loadPaymentMethods();
    }
  };

  const handleConfirmChangeMethod = async () => {
    if (!newPaymentMethod) {
      setError('Silakan pilih metode pembayaran');
      return;
    }

    try {
      setCreating(true);
      setError('');
      setSuccess('');

      // Create new payment with new method
      const newPaymentData = {
        bill_id: billId!,
        payment_method: newPaymentMethod,
        customer_name: formData.customer_name,
        customer_email: formData.customer_email,
        customer_phone: formData.customer_phone,
        replace_existing: true
      };

      const response = await publicPaymentAPI.createPayment(newPaymentData);

      if (response.success) {
        setPaymentStatus({
          reference: response.data.reference,
          status: response.data.status,
          payment_method_name: response.data.payment_method_name,
          amount: response.data.amount,
          expired_time: response.data.expired_time,
          paid_time: response.data.paid_time,
          checkout_url: response.data.checkout_url,
          pay_code: response.data.pay_code,
          pay_url: response.data.pay_url
        });
        setShowChangeMethod(false);
        setStep('payment-created');
        setSuccess('Pembayaran baru berhasil dibuat dengan metode yang dipilih! Silakan selesaikan pembayaran Anda.');
      } else {
        setError(response.message || 'Gagal membuat pembayaran baru');
      }
    } catch (err: any) {
      setError(err.message || 'Gagal membuat pembayaran baru');
    } finally {
      setCreating(false);
    }
  };

  const handleCancelChangeMethod = () => {
    setShowChangeMethod(false);
    setNewPaymentMethod('');
    setError('');
  };

  if (loading && !bill) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (!bill) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Card>
          <CardContent>
            <Alert severity="error">
              Tagihan tidak ditemukan atau ID tagihan tidak valid.
            </Alert>
            <Box sx={{ mt: 2 }}>
              <Button
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate('/')}
              >
                Kembali
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Card>
        <CardContent>
          <Stack spacing={3}>
            {error && (
              <Alert severity="error" onClose={() => setError('')}>
                {error}
              </Alert>
            )}
            
            {success && (
              <Alert severity="success" onClose={() => setSuccess('')}>
                {success}
              </Alert>
            )}

            {/* Bill Information */}
            <Paper sx={{ p: 3, bgcolor: 'grey.50' }}>
              <Typography variant="h5" gutterBottom>
                Pembayaran Tagihan
              </Typography>
              <Stack spacing={1}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="textSecondary">Nomor Tagihan:</Typography>
                  <Typography variant="body1" fontWeight="bold">{bill.bill_number}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="textSecondary">Pelanggan:</Typography>
                  <Typography variant="body1">{bill.customer.name}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="textSecondary">Paket:</Typography>
                  <Typography variant="body1">{bill.package.name}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="textSecondary">Periode:</Typography>
                  <Typography variant="body1">
                    {new Date(bill.billing_period_start).toLocaleDateString('id-ID')} - {' '}
                    {new Date(bill.billing_period_end).toLocaleDateString('id-ID')}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="textSecondary">Jatuh Tempo:</Typography>
                  <Typography variant="body1" color="error">
                    {new Date(bill.due_date).toLocaleDateString('id-ID')}
                  </Typography>
                </Box>
                <Divider />
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="h6">Total Tagihan:</Typography>
                  <Typography variant="h6" color="primary">
                    {formatCurrency(bill.total_amount)}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" color="textSecondary">Status:</Typography>
                  <Chip 
                    label={bill.status === 'paid' ? 'LUNAS' : 'BELUM LUNAS'} 
                    color={bill.status === 'paid' ? 'success' : 'warning'}
                    size="small"
                  />
                </Box>
              </Stack>
            </Paper>

            {/* Payment Form or Status */}
            {step === 'form' && bill.status !== 'paid' && (
              <Stack spacing={3}>
                <Typography variant="h6">Informasi Pembayaran</Typography>
                
                <TextField
                  fullWidth
                  label="Nama Lengkap"
                  value={formData.customer_name}
                  onChange={handleInputChange('customer_name')}
                  required
                  placeholder="Masukkan nama lengkap Anda"
                  InputProps={{
                    readOnly: Boolean(bill?.customer?.name),
                  }}
                  sx={{
                    '& .MuiInputBase-input': {
                      backgroundColor: bill?.customer?.name ? 'rgba(0, 0, 0, 0.04)' : 'transparent'
                    }
                  }}
                />

                <TextField
                  fullWidth
                  label="Alamat Email"
                  type="email"
                  value={formData.customer_email}
                  onChange={handleInputChange('customer_email')}
                  required
                  placeholder="email.anda@contoh.com"
                  InputProps={{
                    readOnly: Boolean(bill?.customer?.email),
                  }}
                  sx={{
                    '& .MuiInputBase-input': {
                      backgroundColor: bill?.customer?.email ? 'rgba(0, 0, 0, 0.04)' : 'transparent'
                    }
                  }}
                />

                <TextField
                  fullWidth
                  label="Nomor Telepon"
                  value={formData.customer_phone}
                  onChange={handleInputChange('customer_phone')}
                  placeholder="08123456789"
                  InputProps={{
                    readOnly: Boolean(bill?.customer?.phone),
                  }}
                  sx={{
                    '& .MuiInputBase-input': {
                      backgroundColor: bill?.customer?.phone ? 'rgba(0, 0, 0, 0.04)' : 'transparent'
                    }
                  }}
                />

                <FormControl fullWidth required>
                  <InputLabel>Metode Pembayaran</InputLabel>
                  <Select
                    value={formData.payment_method}
                    label="Metode Pembayaran"
                    onChange={handleSelectChange('payment_method')}
                  >
                    {paymentMethods.map((method) => (
                      <MenuItem key={method.code} value={method.code}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                          <Typography>{method.name}</Typography>
                          <Typography variant="caption" color="textSecondary">
                            Biaya: {formatCurrency(method.fee_customer.flat)}
                            {method.fee_customer.percent > 0 && ` + ${method.fee_customer.percent}%`}
                          </Typography>
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Button
                  variant="contained"
                  size="large"
                  onClick={handleCreatePayment}
                  disabled={creating}
                  startIcon={creating ? <CircularProgress size={16} /> : <PaymentIcon />}
                  fullWidth
                >
                  {creating ? 'Membuat Pembayaran...' : 'Buat Pembayaran'}
                </Button>
              </Stack>
            )}

            {step === 'payment-created' && paymentStatus && (
              <Stack spacing={3}>
                <Typography variant="h6">Informasi Pembayaran</Typography>
                
                {/* Show status alert */}
                {paymentStatus.status === 'PAID' && (
                  <Alert severity="success">
                    Pembayaran telah berhasil diselesaikan!
                  </Alert>
                )}
                
                {paymentStatus.status === 'EXPIRED' && (
                  <Alert severity="error">
                    Pembayaran telah kedaluwarsa. Silakan buat pembayaran baru untuk melanjutkan.
                  </Alert>
                )}
                
                {paymentStatus.status === 'FAILED' && (
                  <Alert severity="error">
                    Pembayaran gagal. Silakan buat pembayaran baru untuk mencoba lagi.
                  </Alert>
                )}
                
                {paymentStatus.status === 'UNPAID' && (
                  <Alert severity="info">
                    Pembayaran menunggu penyelesaian. Silakan selesaikan pembayaran Anda sebelum kedaluwarsa.
                  </Alert>
                )}

                {paymentStatus.status === 'EXPIRED' && (
                  <Alert severity="error">
                    Pembayaran ini telah kedaluwarsa. Silakan buat pembayaran baru untuk melanjutkan.
                  </Alert>
                )}

                {paymentStatus.status === 'FAILED' && (
                  <Alert severity="error">
                    Pembayaran gagal. Silakan buat pembayaran baru untuk mencoba lagi.
                  </Alert>
                )}

                {paymentStatus.status === 'PAID' && (
                  <Alert severity="success">
                    Pembayaran berhasil diselesaikan! Terima kasih atas pembayaran Anda.
                  </Alert>
                )}
                
                <Paper sx={{ p: 3, bgcolor: 'grey.50' }}>
                  <Stack spacing={2}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="h6">Detail Pembayaran</Typography>
                      <Chip 
                        label={paymentStatus.status === 'UNPAID' ? 'BELUM DIBAYAR' : 
                               paymentStatus.status === 'PAID' ? 'LUNAS' :
                               paymentStatus.status === 'EXPIRED' ? 'KEDALUWARSA' :
                               paymentStatus.status === 'FAILED' ? 'GAGAL' : paymentStatus.status} 
                        color={getStatusColor(paymentStatus.status) as any}
                        size="small"
                      />
                    </Box>
                    
                    <Box>
                      <Typography variant="body2">Nomor Referensi</Typography>
                      <Typography variant="h6" fontFamily="monospace">
                        {paymentStatus.reference}
                      </Typography>
                    </Box>
                    
                    <Box>
                      <Typography variant="body2">Metode Pembayaran</Typography>
                      <Typography variant="body1">{paymentStatus.payment_method_name}</Typography>
                    </Box>
                    
                    <Box>
                      <Typography variant="body2">Jumlah</Typography>
                      <Typography variant="h6">{formatCurrency(paymentStatus.amount)}</Typography>
                    </Box>

                    {paymentStatus.pay_code && (
                      <Box>
                        <Typography variant="body2">Kode Pembayaran</Typography>
                        <Typography variant="h5" fontFamily="monospace" color="error.main">
                          {paymentStatus.pay_code}
                        </Typography>
                        <Typography variant="caption">
                          Gunakan kode ini untuk pembayaran
                        </Typography>
                      </Box>
                    )}

                    <Box>
                      <Typography variant="body2">Kedaluwarsa Pada</Typography>
                      <Typography variant="body1" color="error.main" fontWeight="bold">
                        {paymentStatus.expired_time ? 
                          new Date(paymentStatus.expired_time).toLocaleString('id-ID', {
                            weekday: 'long',
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                            timeZoneName: 'short'
                          }) : 
                          'Tidak ada waktu kedaluwarsa'
                        }
                      </Typography>
                      {paymentStatus.expired_time && paymentStatus.status === 'UNPAID' && (
                        <Typography variant="caption" color="warning.main">
                          {(() => {
                            const now = new Date();
                            const expiredTime = new Date(paymentStatus.expired_time);
                            const timeLeft = Math.floor((expiredTime.getTime() - now.getTime()) / 1000);
                            if (timeLeft > 0) {
                              const hours = Math.floor(timeLeft / 3600);
                              const minutes = Math.floor((timeLeft % 3600) / 60);
                              return `Waktu tersisa: ${hours} jam ${minutes} menit`;
                            }
                            return 'Pembayaran telah kedaluwarsa';
                          })()}
                        </Typography>
                      )}
                    </Box>
                  </Stack>
                </Paper>

                {/* Change Payment Method Section */}
                {paymentStatus.status === 'UNPAID' && !showChangeMethod && (
                  <Paper sx={{ p: 2, bgcolor: 'info.light', color: 'info.contrastText' }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography variant="body2">
                        Ingin menggunakan metode pembayaran yang berbeda?
                      </Typography>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<SwapIcon />}
                        onClick={handleChangePaymentMethod}
                        sx={{ color: 'info.dark', borderColor: 'info.dark' }}
                      >
                        Ganti Metode
                      </Button>
                    </Stack>
                  </Paper>
                )}

                {/* Change Payment Method Form */}
                {showChangeMethod && (
                  <Paper sx={{ p: 3, bgcolor: 'warning.light' }}>
                    <Stack spacing={2}>
                      <Typography variant="h6" color="warning.dark">
                        Ganti Metode Pembayaran
                      </Typography>
                      
                      <Alert severity="warning">
                        <Stack spacing={1}>
                          <Typography variant="body2" fontWeight="bold">
                            Pemberitahuan Penting:
                          </Typography>
                          <Typography variant="body2">
                            • Membuat pembayaran baru akan membuat pembayaran saat ini menjadi tidak valid
                          </Typography>
                          <Typography variant="body2">
                            • Anda akan mendapatkan nomor referensi dan waktu kedaluwarsa yang baru
                          </Typography>
                          <Typography variant="body2">
                            • Silakan selesaikan pembayaran baru sebelum kedaluwarsa
                          </Typography>
                        </Stack>
                      </Alert>

                      <FormControl fullWidth required>
                        <InputLabel>Metode Pembayaran Baru</InputLabel>
                        <Select
                          value={newPaymentMethod}
                          label="Metode Pembayaran Baru"
                          onChange={(e) => setNewPaymentMethod(e.target.value)}
                        >
                          {paymentMethods.map((method) => (
                            <MenuItem key={method.code} value={method.code}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                                <Typography>{method.name}</Typography>
                                <Typography variant="caption" color="textSecondary">
                                  Biaya: {formatCurrency(method.fee_customer.flat)}
                                  {method.fee_customer.percent > 0 && ` + ${method.fee_customer.percent}%`}
                                </Typography>
                              </Box>
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>

                      <Stack direction="row" spacing={2}>
                        <Button
                          variant="contained"
                          onClick={handleConfirmChangeMethod}
                          disabled={creating || !newPaymentMethod}
                          startIcon={creating ? <CircularProgress size={16} /> : <PaymentIcon />}
                          fullWidth
                        >
                          {creating ? 'Membuat...' : 'Buat Pembayaran Baru'}
                        </Button>
                        
                        <Button
                          variant="outlined"
                          onClick={handleCancelChangeMethod}
                          disabled={creating}
                        >
                          Batal
                        </Button>
                      </Stack>
                    </Stack>
                  </Paper>
                )}

                <Stack direction="row" spacing={2}>
                  {paymentStatus.status === 'UNPAID' && !showChangeMethod ? (
                    <>
                      <Button
                        variant="contained"
                        startIcon={<LaunchIcon />}
                        onClick={() => {
                          if (paymentStatus.checkout_url) {
                            window.open(paymentStatus.checkout_url, '_blank');
                          } else {
                            setError('URL pembayaran tidak tersedia');
                          }
                        }}
                        disabled={!paymentStatus.checkout_url}
                        fullWidth
                      >
                        {paymentStatus.checkout_url ? 'Bayar Sekarang' : 'URL Pembayaran Tidak Tersedia'}
                      </Button>
                      
                      <Button
                        variant="outlined"
                        startIcon={<RefreshIcon />}
                        onClick={refreshPaymentStatus}
                        disabled={loading}
                      >
                        {loading ? <CircularProgress size={16} /> : 'Perbarui'}
                      </Button>
                    </>
                  ) : paymentStatus.status === 'PAID' ? (
                    <Button
                      variant="outlined"
                      startIcon={<RefreshIcon />}
                      onClick={refreshPaymentStatus}
                      disabled={loading}
                      fullWidth
                    >
                      {loading ? <CircularProgress size={16} /> : 'Perbarui Status'}
                    </Button>
                  ) : paymentStatus.status === 'EXPIRED' || paymentStatus.status === 'FAILED' ? (
                    // For EXPIRED or FAILED status
                    <>
                      <Button
                        variant="contained"
                        startIcon={<PaymentIcon />}
                        onClick={handleCreateNewPayment}
                        fullWidth
                      >
                        Buat Pembayaran Baru
                      </Button>
                      
                      <Button
                        variant="outlined"
                        startIcon={<RefreshIcon />}
                        onClick={refreshPaymentStatus}
                        disabled={loading}
                      >
                        {loading ? <CircularProgress size={16} /> : 'Perbarui'}
                      </Button>
                    </>
                  ) : null}
                </Stack>
              </Stack>
            )}

            {bill.status === 'paid' && (
              <Paper sx={{ p: 3, bgcolor: 'success.light' }}>
                <Stack spacing={2} alignItems="center">
                  <Typography variant="h6" color="success.dark">
                    ✅ Tagihan ini telah dibayar
                  </Typography>
                  <Typography variant="body1" color="success.dark">
                    Terima kasih atas pembayaran Anda!
                  </Typography>
                </Stack>
              </Paper>
            )}
          </Stack>
        </CardContent>
      </Card>
    </Container>
  );
};

export default PublicPaymentPage;
